import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import Layout from '../../components/layout/Layout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart3, 
  ArrowLeft, 
  DollarSign, 
  ShoppingCart, 
  Users, 
  TrendingUp,
  Tag,
  Package,
  Coins,
  AlertTriangle,
  CircleDollarSign,
  ArrowUpCircle,
  ArrowDownCircle,
  Shield,
  Eye,
  XCircle
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const FinanceAnalyticsPage = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('sales');
  
  // Sales data
  const [overview, setOverview] = useState(null);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [couponPerformance, setCouponPerformance] = useState([]);
  const [planPerformance, setPlanPerformance] = useState([]);
  
  // Coin data
  const [coinOverview, setCoinOverview] = useState(null);
  const [coinTrend, setCoinTrend] = useState([]);
  const [topEarners, setTopEarners] = useState([]);
  const [flaggedUsers, setFlaggedUsers] = useState([]);
  const [earnerPeriod, setEarnerPeriod] = useState('week');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userActivity, setUserActivity] = useState(null);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'coins') {
      fetchCoinAnalytics();
    }
  }, [activeTab, earnerPeriod]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const token = token;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const baseURL = process.env.REACT_APP_BACKEND_URL;

      // Fetch all analytics data
      const [overviewRes, trendRes, couponRes, planRes] = await Promise.all([
        fetch(`${baseURL}/api/admin/analytics/overview`, { headers }),
        fetch(`${baseURL}/api/admin/analytics/revenue-trend?days=30`, { headers }),
        fetch(`${baseURL}/api/admin/analytics/coupon-performance`, { headers }),
        fetch(`${baseURL}/api/admin/analytics/plan-performance`, { headers })
      ]);

      if (overviewRes.ok) setOverview(await overviewRes.json());
      if (trendRes.ok) setRevenueTrend(await trendRes.json());
      if (couponRes.ok) setCouponPerformance(await couponRes.json());
      if (planRes.ok) setPlanPerformance(await planRes.json());
    } catch (error) {
      console.error('Failed to fetch analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCoinAnalytics = async () => {
    try {
      const token = token;
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const baseURL = process.env.REACT_APP_BACKEND_URL;

      const [overviewRes, trendRes, earnersRes, flaggedRes] = await Promise.all([
        fetch(`${baseURL}/api/admin/coin-analytics/overview`, { headers }),
        fetch(`${baseURL}/api/admin/coin-analytics/trend?days=30`, { headers }),
        fetch(`${baseURL}/api/admin/coin-analytics/top-earners?period=${earnerPeriod}`, { headers }),
        fetch(`${baseURL}/api/admin/coin-analytics/flagged-users`, { headers })
      ]);

      if (overviewRes.ok) setCoinOverview(await overviewRes.json());
      if (trendRes.ok) setCoinTrend(await trendRes.json());
      if (earnersRes.ok) setTopEarners(await earnersRes.json());
      if (flaggedRes.ok) setFlaggedUsers(await flaggedRes.json());
    } catch (error) {
      console.error('Failed to fetch coin analytics:', error);
    }
  };

  const fetchUserActivity = async (userId) => {
    try {
      const token = token;
      const baseURL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${baseURL}/api/admin/coin-analytics/user-activity/${userId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setUserActivity(data);
        setSelectedUser(userId);
      }
    } catch (error) {
      console.error('Failed to fetch user activity:', error);
    }
  };

  const clearUserFlags = async (userId) => {
    try {
      const token = token;
      const baseURL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${baseURL}/api/admin/coin-analytics/clear-flags/${userId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        toast.success('Flags cleared successfully');
        fetchCoinAnalytics();
        setUserActivity(null);
        setSelectedUser(null);
      }
    } catch (error) {
      toast.error('Failed to clear flags');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getFlagBadgeColor = (flagType) => {
    switch (flagType) {
      case 'monthly_limit': return 'bg-red-100 text-red-700';
      case 'daily_limit': return 'bg-orange-100 text-orange-700';
      case 'rapid_earning': return 'bg-yellow-100 text-yellow-700';
      case 'burst_pattern': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto p-6">
          <div className="flex justify-center items-center h-64">
            <div className="w-12 h-12 border-4 border-teal-200 border-t-teal-600 rounded-full animate-spin" />
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="container mx-auto p-6">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/admin/finances')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Finances
        </Button>
        
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <BarChart3 className="w-8 h-8" />
          Sales Overview & Analytics
        </h1>
        <p className="text-gray-600 mt-2">Revenue trends, sales reports, and financial insights</p>
      </div>

      {/* Main Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2 lg:w-[400px]">
          <TabsTrigger value="sales" data-testid="tab-sales">
            <DollarSign className="w-4 h-4 mr-2" />
            Sales & Revenue
          </TabsTrigger>
          <TabsTrigger value="coins" data-testid="tab-coins">
            <Coins className="w-4 h-4 mr-2" />
            Coin Economy
          </TabsTrigger>
        </TabsList>

        {/* Sales Tab */}
        <TabsContent value="sales" className="space-y-6">
          {/* Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Revenue</p>
                    <p className="text-2xl font-bold">
                      {overview ? formatCurrency(overview.total_revenue) : '₹0'}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                    <DollarSign className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Transactions</p>
                    <p className="text-2xl font-bold">{overview?.total_transactions || 0}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {overview?.success_rate || 0}% success rate
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Paid Users</p>
                    <p className="text-2xl font-bold">{overview?.paid_users || 0}</p>
                  </div>
                  <div className="h-12 w-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users className="w-6 h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Avg Order Value</p>
                    <p className="text-2xl font-bold">
                      {overview ? formatCurrency(overview.average_order_value) : '₹0'}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-orange-100 rounded-full flex items-center justify-center">
                    <TrendingUp className="w-6 h-6 text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Revenue Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Revenue Trend (Last 30 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {revenueTrend.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No revenue data available yet</p>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Date</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {revenueTrend.slice(-10).reverse().map((item) => (
                        <TableRow key={item.date}>
                          <TableCell>{new Date(item.date).toLocaleDateString('en-IN', { 
                            day: 'numeric', 
                            month: 'short', 
                            year: 'numeric' 
                          })}</TableCell>
                          <TableCell className="text-right font-medium">
                            {formatCurrency(item.revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Coupon Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Tag className="w-5 h-5" />
                Coupon Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {couponPerformance.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No coupon usage data yet</p>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Coupon Code</TableHead>
                        <TableHead>Discount</TableHead>
                        <TableHead>Times Used</TableHead>
                        <TableHead>Revenue Generated</TableHead>
                        <TableHead>Discount Given</TableHead>
                        <TableHead>Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {couponPerformance.map((coupon) => (
                        <TableRow key={coupon.code}>
                          <TableCell className="font-mono font-bold">{coupon.code}</TableCell>
                          <TableCell>
                            <Badge className={coupon.discount_type === 'percentage' ? 'bg-green-600' : 'bg-blue-600'}>
                              {coupon.discount_type === 'percentage' 
                                ? `${coupon.discount_value}% OFF` 
                                : `₹${coupon.discount_value} OFF`}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <span className="font-semibold">{coupon.times_used}</span>
                            {coupon.max_uses ? ` / ${coupon.max_uses}` : ''}
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(coupon.revenue_generated)}
                          </TableCell>
                          <TableCell className="text-red-600">
                            -{formatCurrency(coupon.total_discount_given)}
                          </TableCell>
                          <TableCell>
                            <Badge variant={coupon.is_active ? "success" : "secondary"}>
                              {coupon.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Plan Performance */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Package className="w-5 h-5" />
                Plan Performance
              </CardTitle>
            </CardHeader>
            <CardContent>
              {planPerformance.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No plan sales data yet</p>
              ) : (
                <div className="border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Plan Name</TableHead>
                        <TableHead>Price</TableHead>
                        <TableHead>Sales Count</TableHead>
                        <TableHead className="text-right">Revenue</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {planPerformance.map((plan, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="font-medium">{plan.plan_name}</TableCell>
                          <TableCell>{formatCurrency(plan.plan_price)}</TableCell>
                          <TableCell>
                            <span className="font-semibold">{plan.sales_count}</span> sales
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {formatCurrency(plan.revenue)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Coins Tab */}
        <TabsContent value="coins" className="space-y-6">
          {/* Coin Overview Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card data-testid="coin-circulating">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Circulating</p>
                    <p className="text-2xl font-bold text-amber-600">
                      {coinOverview?.total_circulating || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      ≈ {formatCurrency(coinOverview?.circulating_value_inr || 0)}
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <CircleDollarSign className="w-6 h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="coin-earned">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Earned</p>
                    <p className="text-2xl font-bold text-green-600">
                      {coinOverview?.total_earned || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      {coinOverview?.users_with_coins || 0} users with coins
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                    <ArrowUpCircle className="w-6 h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="coin-redeemed">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total Redeemed</p>
                    <p className="text-2xl font-bold text-blue-600">
                      {coinOverview?.total_redeemed || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      @ ₹{coinOverview?.coin_value_inr || 10}/coin
                    </p>
                  </div>
                  <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <ArrowDownCircle className="w-6 h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card data-testid="coin-flagged">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Flagged Users</p>
                    <p className={`text-2xl font-bold ${coinOverview?.flagged_users > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      {coinOverview?.flagged_users || 0}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Suspicious activity
                    </p>
                  </div>
                  <div className={`h-12 w-12 rounded-full flex items-center justify-center ${coinOverview?.flagged_users > 0 ? 'bg-red-100' : 'bg-gray-100'}`}>
                    <AlertTriangle className={`w-6 h-6 ${coinOverview?.flagged_users > 0 ? 'text-red-600' : 'text-gray-400'}`} />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Coin Activity Trend Chart */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" />
                Coin Activity Trend (Last 30 Days)
              </CardTitle>
              <CardDescription>Daily coins earned vs redeemed</CardDescription>
            </CardHeader>
            <CardContent>
              {coinTrend.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No coin activity data yet</p>
              ) : (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={coinTrend}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                        fontSize={12}
                      />
                      <YAxis fontSize={12} />
                      <Tooltip 
                        labelFormatter={(value) => new Date(value).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                      />
                      <Legend />
                      <Line type="monotone" dataKey="earned" stroke="#22c55e" strokeWidth={2} name="Earned" dot={false} />
                      <Line type="monotone" dataKey="redeemed" stroke="#3b82f6" strokeWidth={2} name="Redeemed" dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid lg:grid-cols-2 gap-6">
            {/* Top Earners */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Users className="w-5 h-5" />
                      Top Earners
                    </CardTitle>
                    <CardDescription>Users with most coins earned</CardDescription>
                  </div>
                  <Select value={earnerPeriod} onValueChange={setEarnerPeriod}>
                    <SelectTrigger className="w-[120px] h-8">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="week">This Week</SelectItem>
                      <SelectItem value="month">This Month</SelectItem>
                      <SelectItem value="all">All Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </CardHeader>
              <CardContent>
                {topEarners.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No earning data yet</p>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {topEarners.map((user, idx) => (
                      <div 
                        key={user.user_id}
                        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer hover:bg-gray-50 ${
                          user.is_flagged ? 'border-red-200 bg-red-50' : ''
                        }`}
                        onClick={() => fetchUserActivity(user.user_id)}
                      >
                        <div className="flex items-center gap-3">
                          <span className={`w-7 h-7 rounded-full flex items-center justify-center text-sm font-bold ${
                            idx < 3 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {idx + 1}
                          </span>
                          <div>
                            <p className="font-medium text-sm">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.indos_number}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {user.is_flagged && (
                            <AlertTriangle className="w-4 h-4 text-red-500" />
                          )}
                          <Badge className="bg-amber-100 text-amber-700">
                            +{user.coins_earned}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Flagged Users */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-red-500" />
                  Flagged Users
                </CardTitle>
                <CardDescription>Users with suspicious coin activity</CardDescription>
              </CardHeader>
              <CardContent>
                {flaggedUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <Shield className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-gray-500">No suspicious activity detected</p>
                  </div>
                ) : (
                  <div className="space-y-3 max-h-[400px] overflow-y-auto">
                    {flaggedUsers.map((user) => (
                      <div 
                        key={user.user_id}
                        className="p-3 rounded-lg border border-red-200 bg-red-50 cursor-pointer hover:bg-red-100"
                        onClick={() => fetchUserActivity(user.user_id)}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <p className="font-medium text-sm">{user.name}</p>
                            <p className="text-xs text-gray-500">{user.indos_number} • {user.email}</p>
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              fetchUserActivity(user.user_id);
                            }}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                        </div>
                        <div className="flex flex-wrap gap-1">
                          {user.flags.map((flag, idx) => (
                            <Badge key={idx} className={`text-xs ${getFlagBadgeColor(flag.type)}`}>
                              {flag.type.replace('_', ' ')}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Balance: {user.coins_balance} • Total Earned: {user.coins_earned_total}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* User Activity Detail Modal/Panel */}
          {userActivity && (
            <Card className="border-2 border-teal-500">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Eye className="w-5 h-5" />
                      User Activity Detail: {userActivity.user?.name}
                    </CardTitle>
                    <CardDescription>
                      {userActivity.user?.indos_number} • {userActivity.user?.email}
                    </CardDescription>
                  </div>
                  <div className="flex gap-2">
                    {userActivity.user?.coin_flags?.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-green-600 border-green-300"
                        onClick={() => clearUserFlags(selectedUser)}
                      >
                        <XCircle className="w-4 h-4 mr-1" />
                        Clear Flags
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setUserActivity(null); setSelectedUser(null); }}
                    >
                      Close
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-4 gap-4 mb-6">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-xs text-gray-500">Current Balance</p>
                    <p className="text-xl font-bold">{userActivity.user?.coins_balance || 0}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-xs text-gray-500">Earned Today</p>
                    <p className="text-xl font-bold text-green-600">
                      {userActivity.earned_today} / {userActivity.daily_limit}
                    </p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-xs text-gray-500">Earned This Month</p>
                    <p className="text-xl font-bold text-blue-600">
                      {userActivity.earned_this_month} / {userActivity.monthly_limit}
                    </p>
                  </div>
                  <div className="p-3 bg-amber-50 rounded-lg">
                    <p className="text-xs text-gray-500">Total Earned</p>
                    <p className="text-xl font-bold text-amber-600">
                      {userActivity.user?.coins_earned_total || 0}
                    </p>
                  </div>
                </div>

                {/* Flags */}
                {userActivity.user?.coin_flags?.length > 0 && (
                  <div className="mb-4 p-3 bg-red-50 rounded-lg">
                    <p className="text-sm font-medium text-red-700 mb-2">Active Flags:</p>
                    <div className="flex flex-wrap gap-2">
                      {userActivity.user.coin_flags.map((flag, idx) => (
                        <div key={idx} className={`px-3 py-1 rounded-full text-xs ${getFlagBadgeColor(flag.type)}`}>
                          <span className="font-medium">{flag.type.replace('_', ' ')}</span>
                          <span className="ml-2 opacity-70">
                            {new Date(flag.timestamp).toLocaleDateString()}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Transactions */}
                <div>
                  <h4 className="font-medium mb-3">Recent Transactions</h4>
                  <div className="border rounded-lg max-h-[300px] overflow-y-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Date</TableHead>
                          <TableHead>Type</TableHead>
                          <TableHead>Reason</TableHead>
                          <TableHead className="text-right">Coins</TableHead>
                          <TableHead className="text-right">Balance After</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {userActivity.transactions?.map((tx) => (
                          <TableRow key={tx.id}>
                            <TableCell className="text-xs">
                              {new Date(tx.created_at).toLocaleString('en-IN')}
                            </TableCell>
                            <TableCell>
                              <Badge className={tx.transaction_type === 'earn' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                                {tx.transaction_type}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm">{tx.reason}</TableCell>
                            <TableCell className={`text-right font-medium ${tx.coins > 0 ? 'text-green-600' : 'text-blue-600'}`}>
                              {tx.coins > 0 ? '+' : ''}{tx.coins}
                            </TableCell>
                            <TableCell className="text-right">{tx.balance_after}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
    </Layout>
  );
};

export default FinanceAnalyticsPage;
